/**
 * Convert an EasyEDA / LCSC component into CIRCAD parts.
 *
 * EasyEDA's free component API (used by LCSC and JLCPCB) returns a part as a
 * bundle of `~`-delimited "shape" strings â€” one set for the schematic symbol,
 * one for the PCB footprint. This turns the footprint set into a CIRCAD
 * component, and builds a tidy generic box symbol to go with it.
 *
 * The format is not officially documented; the field orders here follow the
 * community reverse-engineering (easyeda2kicad). Two things to know:
 *
 *   - One EasyEDA unit is 10 mil (0.01"), so a coordinate in inches is
 *     `(value - origin) * 0.01`. The origin is the `head.x/head.y` reference.
 *   - EasyEDA's Y axis points down; CIRCAD's points up, so Y is negated.
 *
 * These constants may need a nudge once tested against a real part â€” they are
 * isolated at the top for exactly that reason.
 */

import {
  type ArcEntity, type ComponentEntity, type Entity, type LineEntity,
  type PadEntity, type PadType, type TextEntity, newId,
} from '../model.ts';

/** Inches per EasyEDA unit (1 unit = 10 mil). */
const UNIT = 0.01;

/** EasyEDA layer id â†’ CIRCAD standard-stack layer number, or -1 to drop. */
const LAYER: Record<number, number> = {
  1: 7,   // Top copper
  2: 8,   // Bottom copper
  3: 3,   // Top silk
  4: 4,   // Bottom silk
  5: 5,   // Top paste  â†’ Top mask (no dedicated paste layer in the stock stack)
  6: 6,   // Bottom paste â†’ Bottom mask
  7: 5,   // Top solder mask
  8: 6,   // Bottom solder mask
  11: 0,  // Multi-layer (through-hole) â†’ pad master
  13: 3,  // Top assembly â†’ top silk
  15: 3,  // Top silk (newer id)
};

// ---------------------------------------------------------------------------
// Response shapes (loose â€” the API carries far more than we read)
// ---------------------------------------------------------------------------

interface EasyDataStr {
  head?: { x?: number | string; y?: number | string; c_para?: Record<string, string> };
  shape?: string[];
}
export interface EasyEdaResult {
  title?: string;
  description?: string;
  lcsc?: { number?: string };
  dataStr?: EasyDataStr;
  packageDetail?: { title?: string; dataStr?: EasyDataStr };
}
export interface EasyEdaResponse {
  success?: boolean;
  result?: EasyEdaResult;
  message?: string;
  /** Proxy diagnostics when no endpoint yielded the part. */
  tried?: { url: string; status: number }[];
}

export interface ConvertedPart {
  name: string;
  description: string;
  footprintName: string;
  footprint: ComponentEntity | null;
  symbol: ComponentEntity | null;
}

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

export function convertEasyEda(resp: EasyEdaResponse): ConvertedPart {
  const result = resp.result;
  if (!result) throw new Error(resp.message || 'No part data in the response.');

  const lcsc = result.lcsc?.number ?? '';
  const para = result.dataStr?.head?.c_para ?? {};
  const rawName = (para.name || result.title || lcsc || 'LCSC part').trim();
  const name = sanitiseName(rawName);
  const description = (result.description || result.title || '').trim();

  const ref = (para.pre || '').trim();
  const value = partValue(para, name);

  // Read the symbol's pins before the footprint, so the footprint can be given
  // the pin names too — a pad on the board should say which signal it is, not
  // just which number.
  let pins = symbolPins(result.dataStr);

  const pkg = result.packageDetail?.dataStr;
  const footprintName = sanitiseName(
    (result.packageDetail?.title || para.package || name).trim());
  const footprint = pkg
    ? convertFootprint(pkg, footprintName, pinNames(pins), ref, value)
    : null;

  // Pins come from EasyEDA's own schematic symbol where it has one, which is
  // where the pin *names* live — VCC, GND, RESET — along with the side the
  // vendor put them on. Only when there is no symbol do we fall back to the
  // footprint, which knows pin numbers and nothing else.
  if (pins.length === 0) {
    const numbers = footprint
      ? footprint.children.filter((c): c is PadEntity => c.kind === 'pad' && !!c.pin)
          .map((p) => p.pin!)
      : [];
    pins = dedupe(numbers).map((n) => ({ number: n, name: '', x: 0, y: 0 }));
  }

  const symbol = buildBoxSymbol(name, footprintName, pins, ref || undefined, value);

  return {
    name,
    description: describe(description, para, lcsc),
    footprintName,
    footprint,
    symbol,
  };
}

/** A pin as EasyEDA describes it: number, label, and where the vendor put it. */
export interface EasyPin {
  number: string;
  /** The pin's name — what makes a symbol readable. May be empty. */
  name: string;
  x: number;
  y: number;
}

/**
 * Pins from an EasyEDA schematic symbol.
 *
 * A `P` shape is a run of `^^`-separated segments: the pin itself, its dot, its
 * path, then the *name* and *number* labels. The name is the useful part and is
 * the thing the old footprint-only route threw away.
 *
 *   P~show~electric~num~x~y~rot~id~locked ^^ dot ^^ path ^^ name… ^^ number…
 *   name/number segment: show~x~y~rot~TEXT~anchor~font~size
 */
export function symbolPins(data: EasyDataStr | undefined): EasyPin[] {
  const out: EasyPin[] = [];
  for (const raw of data?.shape ?? []) {
    if (!/^P~/.test(raw)) continue;
    const segs = raw.split('^^');
    const head = (segs[0] ?? '').split('~');
    const nameSeg = (segs[3] ?? '').split('~');
    const numSeg = (segs[4] ?? '').split('~');

    // The number is more reliably the label than the head field, which some
    // parts leave as a SPICE index rather than the printed pin number.
    const number = (numSeg[4] ?? head[3] ?? '').trim();
    if (!number) continue;
    out.push({
      number,
      name: (nameSeg[4] ?? '').trim(),
      x: num(head[4]),
      y: num(head[5]),
    });
  }
  return out;
}

/** The part's value: what a schematic prints under the reference designator. */
function partValue(para: Record<string, string>, fallback: string): string {
  for (const key of ['Value', 'value', 'name', 'Name']) {
    const v = (para[key] ?? '').trim();
    if (v) return v;
  }
  return fallback;
}

/**
 * A description carrying what the catalogue can actually use — the vendor's
 * text plus manufacturer, part number and package, which is most of what makes
 * a library entry worth having.
 */
function describe(base: string, para: Record<string, string>, lcsc: string): string {
  const bits: string[] = [];
  if (base) bits.push(base);
  const mfr = (para['BOM_Manufacturer'] ?? para['Manufacturer'] ?? '').trim();
  const mpn = (para['BOM_Manufacturer Part'] ?? para['Manufacturer Part'] ?? '').trim();
  if (mpn) bits.push(mfr ? `${mfr} ${mpn}` : mpn);
  else if (mfr) bits.push(mfr);
  const pkg = (para.package ?? '').trim();
  if (pkg) bits.push(pkg);
  if (lcsc) bits.push(lcsc);
  // Duplicates are common — the title often repeats the part number.
  return dedupe(bits).join(' · ');
}

// ---------------------------------------------------------------------------
// Footprint
// ---------------------------------------------------------------------------

/** Pin number → pin name, for labelling a footprint's pads. */
function pinNames(pins: EasyPin[]): Map<string, string> {
  const m = new Map<string, string>();
  for (const p of pins) if (p.name) m.set(p.number, p.name);
  return m;
}

function convertFootprint(
  data: EasyDataStr, name: string,
  names: Map<string, string> = new Map(), refPrefix = '', value = '',
): ComponentEntity | null {
  const ox = num(data.head?.x);
  const oy = num(data.head?.y);
  const x = (v: unknown): number => (num(v) - ox) * UNIT;
  const y = (v: unknown): number => -(num(v) - oy) * UNIT;
  const size = (v: unknown): number => Math.abs(num(v)) * UNIT;

  const children: Entity[] = [];

  for (const raw of data.shape ?? []) {
    const f = raw.split('~');
    try {
      switch (f[0]) {
        case 'PAD': children.push(...pad(f, x, y, size, names)); break;
        case 'TRACK': children.push(...track(f, x, y, size)); break;
        case 'CIRCLE': children.push(circle(f, x, y, size)); break;
        case 'TEXT': { const t = text(f, x, y, size); if (t) children.push(t); break; }
        case 'HOLE': children.push(hole(f, x, y, size)); break;
        default: break; // ARC, SOLIDREGION, SVGNODE (3D), VIA rare â€” skipped
      }
    } catch { /* one malformed shape must not sink the whole footprint */ }
  }

  if (children.length === 0) return null;
  return finish(name, children, refPrefix, value);
}

/** PAD~shape~cx~cy~w~h~layerId~net~number~holeR~points~rot~id~holeLen~holePt~plated */
function pad(
  f: string[], x: (v: unknown) => number, y: (v: unknown) => number, size: (v: unknown) => number,
  names: Map<string, string> = new Map(),
): PadEntity[] {
  const shape = (f[1] ?? '').toUpperCase();
  const cx = x(f[2]);
  const cy = y(f[3]);
  const w = size(f[4]);
  const h = size(f[5]);
  const layerId = int(f[6]);
  const pin = (f[8] ?? '').trim();
  const holeR = num(f[9]);
  const rot = num(f[11]);

  const through = holeR > 0 || layerId === 11;
  const rect = shape === 'RECT' || shape === 'POLYGON';
  const type: PadType = through ? (rect ? 'T2' : 'T1') : (rect ? 'S2' : 'S1');
  const layer = through ? 0 : (LAYER[layerId] ?? 7);

  const e: PadEntity = {
    kind: 'pad', id: newId(), layer, type,
    x: cx, y: cy, sizeX: w, sizeY: h,
  };
  if (pin) e.pin = pin;
  // The pin's name rides along in the free-text Data field, which is what
  // CIRCAD's `Data:` is for and round-trips through the writer — so a board pad
  // knows it is VCC, not merely pin 8.
  const label = pin ? names.get(pin) : undefined;
  if (label) e.data = label;
  if (holeR > 0) e.hole = holeR * 2 * UNIT;
  if (rot) e.rotation = rot;
  return [e];
}

/** TRACK~width~layerId~net~points~id â€” points are "x y x y â€¦". */
function track(
  f: string[], x: (v: unknown) => number, y: (v: unknown) => number, size: (v: unknown) => number,
): LineEntity[] {
  const width = size(f[1]) || 0.006;
  const layer = LAYER[int(f[2])] ?? 3;
  const nums = (f[4] ?? '').trim().split(/\s+/).map(Number);
  const out: LineEntity[] = [];
  for (let i = 0; i + 3 < nums.length; i += 2) {
    out.push({
      kind: 'line', id: newId(), layer, width,
      x1: x(nums[i]), y1: y(nums[i + 1]), x2: x(nums[i + 2]), y2: y(nums[i + 3]),
    });
  }
  return out;
}

/** CIRCLE~cx~cy~r~strokeW~layerId~net~id â€” a full circle as a 360Â° arc. */
function circle(
  f: string[], x: (v: unknown) => number, y: (v: unknown) => number, size: (v: unknown) => number,
): ArcEntity {
  return {
    kind: 'arc', id: newId(), layer: LAYER[int(f[5])] ?? 3,
    width: size(f[4]) || 0.006,
    cx: x(f[1]), cy: y(f[2]), radius: size(f[3]), angle: 0, sweep: 360,
  };
}

/** HOLE~cx~cy~radius~id â€” a non-plated mounting hole (a drilled pad master pad). */
function hole(
  f: string[], x: (v: unknown) => number, y: (v: unknown) => number, size: (v: unknown) => number,
): PadEntity {
  const r = size(f[3]);
  return {
    kind: 'pad', id: newId(), layer: 0, type: 'T1',
    x: x(f[1]), y: y(f[2]), sizeX: r * 2, sizeY: r * 2, hole: r * 2,
  };
}

/** TEXT~type~x~y~strokeW~rot~mirror~layerId~net~fontSize~string~â€¦ */
function text(
  f: string[], x: (v: unknown) => number, y: (v: unknown) => number, size: (v: unknown) => number,
): TextEntity | null {
  const str = (f[10] ?? '').trim();
  if (!str) return null;
  const height = size(f[9]) || 0.04;
  const e: TextEntity = {
    kind: 'text', id: newId(), layer: LAYER[int(f[7])] ?? 3,
    x: x(f[2]), y: y(f[3]), height, text: str,
  };
  const rot = num(f[5]);
  if (rot) e.rotation = rot;
  return e;
}

// ---------------------------------------------------------------------------
// Generic box symbol
// ---------------------------------------------------------------------------

/**
 * A rectangular schematic symbol with the footprint's pins down its two sides,
 * on a 0.1" pitch. Not the manufacturer's artwork, but a correct, placeable
 * symbol that carries the right pin numbers and maps to the footprint.
 */
function buildBoxSymbol(
  name: string, footprintName: string, pins: EasyPin[],
  refPrefix?: string, value?: string,
): ComponentEntity {
  const SYM = 1;   // Symbols layer
  const NUM = 5;   // Pin numbers layer
  const NAME = 4;  // Pin names layer

  // Split the pins the way the vendor drew them — those on the left of the
  // symbol stay on the left — and keep each side in its drawn top-to-bottom
  // order. Parts whose pins carry no position fall back to halving the list.
  const positioned = pins.some((p) => p.x !== 0 || p.y !== 0);
  let leftPins: EasyPin[];
  let rightPins: EasyPin[];
  if (positioned) {
    const xs = pins.map((p) => p.x);
    const mid = (Math.min(...xs) + Math.max(...xs)) / 2;
    const byY = (a: EasyPin, b: EasyPin): number => a.y - b.y; // EasyEDA y grows downward
    leftPins = pins.filter((p) => p.x <= mid).sort(byY);
    rightPins = pins.filter((p) => p.x > mid).sort(byY);
    // Everything on one side reads as a column; split it so the box stays square.
    if (rightPins.length === 0) {
      const half = Math.ceil(leftPins.length / 2);
      rightPins = leftPins.slice(half);
      leftPins = leftPins.slice(0, half);
    }
  } else {
    const half = Math.ceil(pins.length / 2);
    leftPins = pins.slice(0, half);
    rightPins = pins.slice(half);
  }

  const perSide = Math.max(leftPins.length, rightPins.length, 1);
  const pitch = 0.1;
  const height = (perSide + 1) * pitch;
  // Wide enough for the longest pin name on each side to sit inside the body.
  const longest = Math.max(0, ...pins.map((p) => p.name.length));
  const halfW = Math.max(0.3, (longest * 0.045) + 0.12);
  const top = (height / 2);
  const children: Entity[] = [];

  const line = (x1: number, y1: number, x2: number, y2: number): void => {
    children.push({ kind: 'line', id: newId(), layer: SYM, width: 0.01, x1, y1, x2, y2 });
  };
  // Body rectangle.
  line(-halfW, -top, halfW, -top);
  line(halfW, -top, halfW, top);
  line(halfW, top, -halfW, top);
  line(-halfW, top, -halfW, -top);

  const place = (pin: EasyPin, row: number, left: boolean): void => {
    const py = top - pitch * (row + 1);
    const bx = left ? -halfW : halfW;
    const ex = left ? bx - pitch : bx + pitch;
    line(bx, py, ex, py); // pin leg
    children.push({
      kind: 'pad', id: newId(), layer: 0, type: 'T0',
      x: ex, y: py, sizeX: 0.01, sizeY: 0.01, pin: pin.number,
    });
    // The pin name inside the body — the label that makes the symbol readable.
    if (pin.name) {
      children.push({
        kind: 'text', id: newId(), layer: NAME,
        x: left ? bx + 0.03 : bx - 0.03, y: py, height: 0.05,
        anchor: left ? 'ML' : 'MR', text: pin.name,
      });
    }
    // The pin number outside it, above the leg, as a schematic conventionally
    // prints it.
    children.push({
      kind: 'text', id: newId(), layer: NUM,
      x: left ? bx - 0.02 : bx + 0.02, y: py + 0.012, height: 0.04,
      anchor: left ? 'BR' : 'BL', text: pin.number,
    });
  };
  leftPins.forEach((p, i) => place(p, i, true));
  rightPins.forEach((p, i) => place(p, i, false));

  // Reference designator and value labels.
  children.push({
    kind: 'text', id: newId(), layer: SYM, x: -halfW, y: top + 0.02, height: 0.06,
    anchor: 'BL', text: '&1',
  });
  if (value) {
    children.push({
      kind: 'text', id: newId(), layer: SYM, x: -halfW, y: -top - 0.02, height: 0.05,
      anchor: 'TL', text: '&2',
    });
  }

  // `pre` from EasyEDA is already a template like "R?"; only add the ? if the
  // caller gave a bare prefix.
  const ref = refPrefix
    ? (refPrefix.includes('?') ? refPrefix : `${refPrefix}?`)
    : 'U?';
  return {
    kind: 'component', id: newId(), layer: 0, name,
    bounds: { x1: -halfW - pitch, y1: -top - 0.1, x2: halfW + pitch, y2: top + 0.1 },
    x: 0, y: 0,
    // &1 is the reference designator, &2 the value — the two parameters a
    // CIRCAD symbol carries and the netlist and BOM read.
    params: value ? { 1: ref, 2: value } : { 1: ref },
    footprint: footprintName,
    children,
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function finish(
  name: string, children: Entity[], refPrefix = '', value = '',
): ComponentEntity {
  let x1 = Infinity; let y1 = Infinity; let x2 = -Infinity; let y2 = -Infinity;
  const grow = (px: number, py: number): void => {
    x1 = Math.min(x1, px); y1 = Math.min(y1, py);
    x2 = Math.max(x2, px); y2 = Math.max(y2, py);
  };
  for (const c of children) {
    if (c.kind === 'pad') { grow(c.x - c.sizeX / 2, c.y - c.sizeY / 2); grow(c.x + c.sizeX / 2, c.y + c.sizeY / 2); }
    else if (c.kind === 'line') { grow(c.x1, c.y1); grow(c.x2, c.y2); }
    else if (c.kind === 'arc') { grow(c.cx - c.radius, c.cy - c.radius); grow(c.cx + c.radius, c.cy + c.radius); }
    else if (c.kind === 'text') grow(c.x, c.y);
  }
  if (!Number.isFinite(x1)) { x1 = y1 = -0.1; x2 = y2 = 0.1; }

  // A footprint carries its reference designator on the silkscreen, the way a
  // board is actually marked, and the same &1/&2 parameters as the symbol so a
  // netlist transfer annotates both halves of the part identically.
  const ref = refPrefix
    ? (refPrefix.includes('?') ? refPrefix : `${refPrefix}?`)
    : 'U?';
  children.push({
    kind: 'text', id: newId(), layer: 3, // Top Silk
    x: x1, y: y2 + 0.02, height: 0.05, anchor: 'BL', text: '&1',
  });

  return {
    kind: 'component', id: newId(), layer: 0, name,
    bounds: { x1, y1, x2: Math.max(x2, x1 + 0.05), y2: y2 + 0.08 },
    x: 0, y: 0,
    params: value ? { 1: ref, 2: value } : { 1: ref },
    children,
  };
}

function num(v: unknown): number {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : 0;
}

function int(v: unknown): number {
  return Math.trunc(num(v));
}

/** Keep names safe for the library (drop control chars, trim length). */
function sanitiseName(s: string): string {
  return s.replace(/[^A-Za-z0-9 ._/*?+()-]/g, '').replace(/\s+/g, ' ').trim().slice(0, 120) || 'LCSC part';
}

function dedupe(pins: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const p of pins) { if (!seen.has(p)) { seen.add(p); out.push(p); } }
  return out;
}
